import {
  AutoPaymentIcon,
  CheckmarkIcon,
  InfoIcon,
  LockIcon,
  Logo,
} from 'components/icons';
import { BasicFrame } from 'components/organisms';
import { ButtonActor } from 'components';
import { frameConfigBasic } from '../../AppConfig';
import { LOGIN_ROUTE } from 'utils/constants';
import { PAYLAND_SEARCHPARAM_CONTINUE } from '../../hooks/usePayment';
import { postMessageToApp } from '../../utils/postMessageToApp';
import { ROUTES } from 'utils/constants/constants_routes';
import { useBankAccountEnding } from 'hooks/useBankAccountEnding';
import { useEffect } from 'react';
import { useErrorManager } from 'InternalLibrary';
import { useGetClientApplication } from '../../api';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../utils/axiosInstance';
import Card from 'components/atoms/Card/Card';
import classes from 'classnames';
import layout from 'scss/variables.module.scss';
import Spacer from 'components/atoms/Spacer/Spacer';
import styles from './Approved.module.scss';
import Text from 'components/atoms/Typography/Text';
import Title from 'components/atoms/Title/Title';
import useClient from '../../hooks/useClient';
import useCrossDomainLoginRedirect from '../../hooks/useCrossDomainLoginRedirect';

export default function Approved() {
  const { client, fetchClient } = useClient();
  const bankAccountEnding = useBankAccountEnding();
  const { fetch: fetchGetClientApplication } = useGetClientApplication();
  const { crossDomainLoginRedirect } = useCrossDomainLoginRedirect();
  const { setError } = useErrorManager();

  const { t } = useTranslation();

  const onSubmit = async () => {
    const continueUrl = await crossDomainLoginRedirect('dashboard', true);
    const url_ko = `${window.location.origin}${ROUTES.PAYMENT_FAILED}?${PAYLAND_SEARCHPARAM_CONTINUE}=${ROUTES.FIRST_LOAN_ONLINE_BANK_APPROVED}`;
    try {
      const response = await axiosInstance.post(
        '/cardpayments/store-credit-card',
        {
          applicationName: 'Vivus.es',
          clientNumber: client?.number,
          failureUrl: url_ko,
          successUrl: `${continueUrl}&autorepay=true` || LOGIN_ROUTE,
          username: client?.fullName,
        },
        {
          headers: {
            Accept: 'application/vnd.cardpayments.v1+json',
            'Content-Type': 'application/vnd.cardpayments.v1+json',
          },
        },
      );
      const payUrl = response?.data?.payformUrl;
      if (payUrl) {
        window.location.assign(payUrl);
      } else {
        setError('defaultTryItLater');
      }
    } catch {
      setError('defaultTryItLater');
    }
  };

  useEffect(() => {
    fetchClient();
    fetchGetClientApplication().then((application) => {
      postMessageToApp('loan_first_main', {
        productNumber: application?.productInfo.number,
        amount: application?.amount,
        term: application?.term,
      });
    });
  }, []);

  return (
    <BasicFrame
      {...frameConfigBasic}
      footerLayout="default"
      className={styles.backgroundImage}
      contentLayoutClassName={layout['frame-x-compact']}
      contentWrapperClassName={layout['flex-column-centered']}
      logoutClassName={styles.logout}
    >
      <Card className={styles.card} dataTest="approved">
        <Spacer
          marginBottom={{ default: '15px' }}
          paddingTop={{ default: '36px' }}
        >
          <Logo data-test="approved-logo" />
        </Spacer>
        <Spacer className={styles.header} paddingTop={{ default: '20px' }}>
          <Spacer marginBottom={{ default: '20px' }}>
            <Title
              textClassName={styles.title}
              iconClassName={styles.iconCheck}
              icon={<CheckmarkIcon />}
              text={t('approved:title')}
              forceColumn
            />
          </Spacer>
          <Text
            className={styles.subTitle}
            dangerouslySetInnerHTML={{
              __html: t('approved:subtitle', {
                amount: client?.openPrincipalAmount || 300,
                last4DigitsAccount: bankAccountEnding || '1234',
              }),
            }}
            dataTest="approved-subtitle"
          />
        </Spacer>

        <Spacer marginTop="50px" marginBottom="15px" maxWidth="560px">
          <Card className={styles.subcard} dataTest="approved-subcard">
            <AutoPaymentIcon
              className={styles.iconWrapper}
              data-test="approved-auto-payment"
            />
            <Spacer
              className={styles.subCardHeader}
              padding={{ default: '25px 30px', xs: '20px' }}
            >
              <Text
                text={t('approved:subcard.title')}
                weight="medium"
                dataTest="approved-subcard-title"
              />
              <Spacer className={styles.subCardBody} paddingTop="24px">
                <ButtonActor
                  buttonProps={{
                    onClick: onSubmit,
                    className: styles.submitBtn,
                    dataTest: 'approved-submit-btn',
                  }}
                  className={styles.submitBtnWrapper}
                >
                  {t('approved:subcard.submitBtn')}
                </ButtonActor>
                <Spacer paddingVertical={'8px'}>
                  <InfoIcon
                    className={classes(styles.greyIcon, styles.greyInfoIcon)}
                    data-test="approved-info-icon"
                  />
                  <Text
                    className={styles.subCardDescription}
                    text={t('approved:subcard.description')}
                    dataTest="approved-subcard-description"
                  />
                </Spacer>
              </Spacer>
            </Spacer>
          </Card>
        </Spacer>
        <Spacer className={styles.footer}>
          <LockIcon
            className={styles.greyIcon}
            data-test="approved-grey-icon"
          />
          <Text
            className={styles.footerText}
            dataTest="approved-footer-text"
            text={t('approved:footer')}
            level={2}
            kind="p"
          />
        </Spacer>
      </Card>
    </BasicFrame>
  );
}
